import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { postSetTransactionWithWallet } from 'services/easyPay'
import useFinishFlow, { FLOWS } from 'utilities/navigation/flow'

/** the flow each entry pays from — it is left, not returned to, on success */
const ENTRY_FLOW = {
    deposit: FLOWS.deposit,
    pay_for_other: FLOWS.payForOther,
    easy_pay: FLOWS.easyPay,
}

/**
 * Pay a fund cash-in entirely from the member's Hamyan wallet.
 *
 * WHY THIS EXISTS: the PWA has shown a «استفاده از کیف پول» switch on the
 * deposit screens for a long time and has never once spent the wallet.
 * `loans/transaction/footer` guarded the wallet branch with
 * `lackOfWallet && lackOfWallet === 0` — `0` is falsy, so the branch was
 * unreachable and every "wallet" payment went to the card in full.
 * `easyPay/payForOther` had the right condition but posted the cash-in body to
 * `/payment/transactions/`, and `modules/easyPay/init/footer` took `isWallet`
 * as a prop and ignored it. `postSetTransactionWithWallet` — the one correct
 * call — was defined in `services/easyPay` and imported nowhere.
 *
 * THE CONTRACT, which is Android's (`MoneypoolPayActivity.sendRequestPay` →
 * `MoneypoolApiCallManager.addMoneypoolWalletCashinPay`): ONE POST to
 * `/api/v1/payment/moneypools/<id>/moneypool-cashins/` carrying a transaction
 * with `source: 'hwt'`. The view then calls `transaction.pay()`
 * (`payment/api/views.py:551`), which for a moneypool wallet transaction
 * debits `payer.withdraw_wallet_balance(total_amount)` and marks it
 * successful. There is no gateway and therefore **no `pay_url`** — redirecting
 * to one is how the old path turned a settled payment into «مشکلی پیش آمده».
 *
 * The caller MUST check the balance covers `amount + commission` first: the
 * debit is `total_amount` and `Member.withdraw_wallet_balance` raises a bare
 * `ValueError` when it does not, which reaches the member as a 500.
 */
export default function useWalletCashin(
    entry: 'deposit' | 'pay_for_other' | 'easy_pay'
) {
    const finishFlow = useFinishFlow(ENTRY_FLOW[entry])

    const { isLoading, mutate } = useMutation({
        mutationFn: (payload: IPaymentData) =>
            postSetTransactionWithWallet(payload),
        onSuccess: (_data, variables) => {
            posthog.capture('payment_wallet_paid', {
                entry,
                amount: (variables as any)?.amount ?? null,
                moneypool_id: (variables as any)?.transaction?.ctx_id ?? null,
            })
            toast.success('پرداخت از کیف پول انجام شد')
            const boxId = (variables as any)?.transaction?.ctx_id
            finishFlow(boxId ? `/fund/${boxId}` : '/funds')
        },
        onError: (error: any) => {
            posthog.capture('payment_wallet_failed', {
                entry,
                status: error?.response?.status ?? null,
            })
            toast.error(
                error?.response?.status === 423
                    ? 'یک پرداخت دیگر در جریان است؛ کمی بعد دوباره تلاش کنید'
                    : 'پرداخت از کیف پول انجام نشد'
            )
        },
    })

    return { isLoading, payFromWallet: mutate }
}

/**
 * The cash-in body for a wallet payment, in the shape the backend's
 * `MoneypoolCashinSerializer` expects.
 *
 * `registrar`, `payer` and `receiver` are deliberately absent: the view derives
 * the registrar from the caller's own poolship and the serializer overwrites
 * `payer`/`receiver` from it (`payment/api/serializers.py:247`). Anything the
 * client sent for them was already being thrown away.
 */
export const walletCashinPayload = ({
    poolshipId,
    moneypoolId,
    amount,
    type,
    destination,
    tag = '',
}: {
    poolshipId: number
    moneypoolId: number
    /** the payment itself, WITHOUT commission — the backend adds it */
    amount: number
    /** cnp | cnc | cnhe | cnhc */
    type: string
    /** `hbb` or `bba`, exactly as `payment/init` reported it for this fund */
    destination: string
    tag?: string
}) => ({
    poolship: poolshipId,
    amount: Number(amount),
    type,
    tag,
    // Only these four survive `TransactionSerializer` — checked against the
    // deployed serializer, which drops the `state` / `state_time` /
    // `transaction_code` / `payer_name` / `receiver_name` / `is_group_pay`
    // blanks the older payloads carried.
    transaction: {
        ctx_id: moneypoolId,
        ctx_type: 'mnp',
        destination,
        source: 'hwt',
    },
})
